import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PanicBackButton from "@/components/PanicBackButton";
import useOfflineQueue from "@/hooks/useOfflineQueue";
import PropertyPhotoRender from "@/components/dashboard/PropertyPhotoRender";
import HunterScanModal from "@/components/HunterScanModal";
import DoctrineBanner from "@/components/DoctrineBanner";

const BACKEND_URL = "";

/**
 * Field Mode Workspace (CAD/CAM)
 * --------------------------------------------------------------------------
 * Mobile-first one-button launch for crew. Pulls specs, tallies materials,
 * logs photos, time-tracks, emits PDF end-of-shift report.
 *
 * Routes:
 *   /field/:projectId        — admin launch (Shannon, via Dashboard)
 *   /crew/:token             — crew launch (magic link, no Google login)
 */

const FieldMode = ({ launchMode, embedded }) => {
  const params = useParams();
  const navigate = useNavigate();
  const projectIdParam = params.projectId;
  const crewToken = params.token;

  // `embedded` (Feb 2, 2026) is treated as admin launch — Field Mode
  // is now embeddable inside Design Studio's Field Mode sub-tab, and
  // the admin session-start effect must fire so we don't stick on
  // "Loading Field Mode…" forever.
  const isAdminLaunch = launchMode === "admin" || embedded === true;
  const isCrewLaunch = launchMode === "crew";

  // Crew gate state
  const [crewProject, setCrewProject] = useState(null);
  const [crewName, setCrewName] = useState("");
  const [crewPhone, setCrewPhone] = useState("");
  const [crewError, setCrewError] = useState("");
  const [crewAuthed, setCrewAuthed] = useState(false);
  const [storedCrewToken, setStoredCrewToken] = useState("");

  // Session state
  const [session, setSession] = useState(null);
  const [project, setProject] = useState(null);
  const [roomType, setRoomType] = useState("");
  const [loading, setLoading] = useState(true);
  const [tallyForm, setTallyForm] = useState({ name: "", quantity: 1, unit_cost: 0 });
  const [tallyTotals, setTallyTotals] = useState({ count: 0, cost: 0 });
  const [endNotes, setEndNotes] = useState("");
  const [endedResult, setEndedResult] = useState(null);
  const [view, setView] = useState("dashboard"); // dashboard | specs | tally | photos | end
  const [calcForm, setCalcForm] = useState({ length_ft: 12, width_ft: 12, ceiling_ft: 9, room_type: "bedroom" });
  const [calcLoading, setCalcLoading] = useState(false);
  const [calcResult, setCalcResult] = useState(null);
  const [toast, setToast] = useState(null);
  const fileInputRef = useRef(null);

  // Voice-caption photo modal state
  const [photoPending, setPhotoPending] = useState(null);   // { b64, dataUrl }
  const [photoCaption, setPhotoCaption] = useState("");
  const [photoListening, setPhotoListening] = useState(false);
  const [photoRecogRef, setPhotoRecogRef] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [typeOnlyMode, setTypeOnlyMode] = useState(false);
  const photoCaptionRef = useRef(null);

  // Photo → Variation renderer modal (opens PropertyPhotoRender in field)
  const [renderOpen, setRenderOpen] = useState(false);
  // Hunter Mode is the HARD-WIRED default view for Field Mode as of Feb 24, 2026.
  // The single-button batch scanner is what Shannon lands on the moment Field
  // Mode boots. The legacy 5-button dashboard is retained ONLY as a fallback
  // for users who explicitly navigate to the advanced tools drawer.
  const [hunterOpen, setHunterOpen] = useState(true);

  // Live FWA attendee feed (admin only, sandbox-safe)
  const [attendees, setAttendees] = useState([]);
  const [attendeesLoading, setAttendeesLoading] = useState(false);
  const loadAttendees = useCallback(async () => {
    if (!isAdminLaunch) return;
    setAttendeesLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/fwaa/attendees/recent?limit=15`, {
        credentials: "include",
      });
      if (r.ok) {
        const d = await r.json();
        setAttendees(d.attendees || []);
      }
    } catch { /* ignore */ }
    setAttendeesLoading(false);
  }, [isAdminLaunch]);
  useEffect(() => { loadAttendees(); }, [loadAttendees]);

  const showToast = (msg, kind = "success") => {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 3000);
  };

  const authHeaders = useCallback(() => {
    const h = { "Content-Type": "application/json" };
    if (isCrewLaunch && storedCrewToken) h["X-Crew-Token"] = storedCrewToken;
    return h;
  }, [isCrewLaunch, storedCrewToken]);

  const fetchOpts = useCallback((opts = {}) => ({
    credentials: isAdminLaunch ? "include" : "omit",
    headers: { ...authHeaders(), ...(opts.headers || {}) },
    ...opts,
  }), [isAdminLaunch, authHeaders]);

  // ─── Ship 3 · Offline Safe Mode ───
  // Queues writes when signal drops, drains on reconnect. Cache lets Field
  // Mode paint last-known session state even when the network is dead.
  const sessionScope = session?.session_id || projectIdParam || crewToken || "global";
  const {
    queuedFetch,
    cacheGet,
    cacheSet,
    queueSize,
    isOnline,
    draining,
    drain,
    lastDrainResult,
  } = useOfflineQueue(`field:queue:${sessionScope}`);

  // Cache session on every mutation so field crew keeps a rendered UI even
  // if they walk into a signal-dead unit.
  useEffect(() => {
    if (session) cacheSet("session", session);
  }, [session, cacheSet]);

  // Toast when the queue drains
  useEffect(() => {
    if (!lastDrainResult) return;
    if (lastDrainResult.drained > 0) {
      setToast({ msg: `✓ Synced ${lastDrainResult.drained} offline action${lastDrainResult.drained === 1 ? '' : 's'}`, kind: "success" });
      setTimeout(() => setToast(null), 3200);
    }
  }, [lastDrainResult]);

  // ─── Crew magic-link landing ───
  const handleCrewJoin = async () => {
    if (!crewName.trim()) { setCrewError("Please type your name"); return; }
    setCrewError("");
    try {
      const r = await fetch(`${BACKEND_URL}/api/field/crew-link/${crewToken}/access`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ crew_name: crewName.trim(), crew_phone: crewPhone.trim() }),
      });
      const d = await r.json();
      if (!r.ok) {
        setCrewError(d.detail || "Magic link not valid");
        return;
      }
      setStoredCrewToken(d.crew_token);
      setCrewProject(d.project);
      setCrewAuthed(true);
      setLoading(false);
    } catch (e) {
      setCrewError("Could not connect");
    }
  };

  // ─── Admin launch: auto-start session ───
  const startSession = useCallback(async (selectedRoom = "") => {
    setLoading(true);
    const projectId = isAdminLaunch ? projectIdParam : crewProject?.project_id;
    if (!projectId) { setLoading(false); return; }
    try {
      const r = await fetch(`${BACKEND_URL}/api/field/session/start`, {
        ...fetchOpts({ method: "POST" }),
        body: JSON.stringify({
          project_id: projectId,
          room_type: selectedRoom || roomType,
          crew_name: isCrewLaunch ? crewName : "",
          crew_phone: isCrewLaunch ? crewPhone : "",
        }),
      });
      const d = await r.json();
      if (r.ok) {
        setSession(d.session);
        setProject({ project_id: projectId, address: d.session.project_address });
        setView("dashboard");
        showToast("✓ Field Mode session started");
      } else {
        showToast(d.detail || "Could not start session", "error");
      }
    } catch (e) {
      showToast("Could not start session", "error");
    } finally {
      setLoading(false);
    }
  }, [isAdminLaunch, isCrewLaunch, projectIdParam, crewProject, roomType, crewName, crewPhone, fetchOpts]);

  // Auto-start when admin opens or crew joins
  useEffect(() => {
    if (isAdminLaunch && projectIdParam && !session) {
      startSession();
    } else if (isCrewLaunch && crewAuthed && !session) {
      startSession();
    }
  }, [isAdminLaunch, isCrewLaunch, crewAuthed, projectIdParam, session, startSession]);

  // ─── Tally ───
  const addTally = async () => {
    if (!tallyForm.name.trim()) { showToast("Type a material name", "error"); return; }
    try {
      const r = await queuedFetch(`${BACKEND_URL}/api/field/session/${session.session_id}/tally`, {
        ...fetchOpts({ method: "POST" }),
        body: JSON.stringify(tallyForm),
      });
      if (r._queued) {
        showToast(`📴 Offline — ${tallyForm.name} queued (${queueSize + 1} in queue)`);
        setTallyForm({ name: "", quantity: 1, unit_cost: 0 });
        return;
      }
      const d = await r.json();
      if (r.ok) {
        showToast(`✓ Added ${tallyForm.name}`);
        setTallyTotals({ count: d.running_total_count, cost: d.running_total_cost });
        setTallyForm({ name: "", quantity: 1, unit_cost: 0 });
        // Reload session
        const sr = await fetch(`${BACKEND_URL}/api/field/session/${session.session_id}`, fetchOpts());
        if (sr.ok) {
          const sd = await sr.json();
          setSession(sd.session);
        }
      } else {
        showToast(d.detail || "Could not add", "error");
      }
    } catch {
      showToast("Could not add", "error");
    }
  };

  const quickAddSpec = (specProduct) => {
    setTallyForm({ name: specProduct.name, quantity: 1, unit_cost: 0 });
    setView("tally");
  };

  const runCalculator = async () => {
    if (!calcForm.length_ft || !calcForm.width_ft) {
      showToast("Enter length and width", "error");
      return;
    }
    setCalcLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/field/session/${session.session_id}/calc-materials`, {
        ...fetchOpts({ method: "POST" }),
        body: JSON.stringify(calcForm),
      });
      const d = await r.json();
      if (r.ok) {
        setCalcResult(d);
        showToast(`✓ ${d.totals.item_count} materials calculated · $${d.totals.total_cost}`);
      } else {
        showToast(d.detail || "Calc failed", "error");
      }
    } catch {
      showToast("Calc failed", "error");
    } finally {
      setCalcLoading(false);
    }
  };

  const quickAddCalc = async (item) => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/field/session/${session.session_id}/tally`, {
        ...fetchOpts({ method: "POST" }),
        body: JSON.stringify({
          name: item.name,
          quantity: item.quantity,
          unit: item.unit,
          unit_cost: item.unit_cost,
          notes: `Auto-calc: ${item.formula}`,
        }),
      });
      const d = await r.json();
      if (r.ok) {
        showToast(`✓ Added ${item.quantity} ${item.unit} ${item.name.split("(")[0].trim()}`);
        setTallyTotals({ count: d.running_total_count, cost: d.running_total_cost });
        const sr = await fetch(`${BACKEND_URL}/api/field/session/${session.session_id}`, fetchOpts());
        if (sr.ok) {
          const sd = await sr.json();
          setSession(sd.session);
        }
      } else {
        showToast(d.detail || "Add failed", "error");
      }
    } catch {
      showToast("Add failed", "error");
    }
  };

  const addAllCalcItems = async () => {
    if (!calcResult?.items?.length) return;
    if (!window.confirm(`Add all ${calcResult.items.length} calculated items to the tally?`)) return;
    for (const item of calcResult.items) {
      await quickAddCalc(item);
    }
  };

  const pushToProDesk = async (storeKey) => {
    if (!calcResult?.items?.length) {
      showToast("Run the calculator first", "error");
      return;
    }
    const storeLabel = { home_depot: "Home Depot Pro Xtra", lowes: "Lowe's For Pros", wayfair: "Wayfair Professional" }[storeKey] || storeKey;
    if (!window.confirm(`Send the ${calcResult.items.length} calculated items + your shift tally to ${storeLabel}?`)) return;
    try {
      const r = await fetch(`${BACKEND_URL}/api/field/session/${session.session_id}/send-to-pro-desk`, {
        ...fetchOpts({ method: "POST" }),
        body: JSON.stringify({ store_key: storeKey, include_tally: true, note: "" }),
      });
      const d = await r.json();
      if (r.ok && d.ok) {
        showToast(`✓ Sent ${d.item_count} items to ${storeLabel}`);
      } else {
        showToast(d.detail || d.error || "Send failed", "error");
      }
    } catch {
      showToast("Send failed", "error");
    }
  };

  // ─── Photo ───
  const onPhotoSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      showToast("Photo must be under 5 MB", "error");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      const b64 = dataUrl.split(",")[1];
      // Stash the photo and open the caption modal instead of auto-uploading
      setPhotoPending({ b64, dataUrl });
      setPhotoCaption("");
    };
    reader.readAsDataURL(file);
    // Reset input so picking the same file twice re-fires
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const togglePhotoListening = () => {
    if (photoListening && photoRecogRef) {
      try { photoRecogRef.stop(); } catch (_) { /* ignore */ }
      setPhotoListening(false);
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      showToast("Voice caption needs Chrome. Switched to Type mode.", "info");
      switchToTypeOnly();
      return;
    }
    const recog = new SpeechRecognition();
    recog.continuous = true;
    recog.interimResults = true;
    recog.lang = "en-US";
    recog.onresult = (event) => {
      let finalText = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          finalText += event.results[i][0].transcript;
        }
      }
      if (finalText.trim()) {
        setPhotoCaption((prev) => (prev ? prev + " " : "") + finalText.trim());
      }
    };
    recog.onerror = () => setPhotoListening(false);
    recog.onend = () => setPhotoListening((v) => {
      if (v) { try { recog.start(); } catch (_) { /* ignore */ } return true; }
      return false;
    });
    recog.start();
    setPhotoRecogRef(recog);
    setPhotoListening(true);
  };

  const cancelPhoto = () => {
    if (photoRecogRef) { try { photoRecogRef.stop(); } catch (_) { /* ignore */ } }
    setPhotoListening(false);
    setPhotoPending(null);
    setPhotoCaption("");
    setTypeOnlyMode(false);
  };

  // Type-instead escape hatch — kills any active voice recognition and
  // focuses the caption textarea so the crew never gets blocked when
  // SpeechRecognition locks up (common on unstable jobsite Wi-Fi or
  // non-Chrome mobile browsers). Requested Feb 12, 2026.
  const switchToTypeOnly = () => {
    if (photoRecogRef) { try { photoRecogRef.stop(); } catch (_) { /* ignore */ } }
    setPhotoListening(false);
    setTypeOnlyMode(true);
    // Defer focus until React re-renders with the textarea unhidden.
    setTimeout(() => {
      if (photoCaptionRef.current) {
        try { photoCaptionRef.current.focus(); } catch (_) { /* ignore */ }
      }
    }, 60);
  };

  const savePhotoWithCaption = async () => {
    if (!photoPending) return;
    if (photoRecogRef) { try { photoRecogRef.stop(); } catch (_) { /* ignore */ } }
    setPhotoListening(false);
    setPhotoUploading(true);
    try {
      const r = await queuedFetch(`${BACKEND_URL}/api/field/session/${session.session_id}/photo`, {
        ...fetchOpts({ method: "POST" }),
        body: JSON.stringify({ image_b64: photoPending.b64, caption: photoCaption.trim() }),
      });
      if (r._queued) {
        showToast(`📴 Offline — photo queued (${queueSize + 1} in queue). Auto-syncs when back online.`);
        cancelPhoto();
        setPhotoUploading(false);
        return;
      }
      const d = await r.json();
      if (r.ok) {
        showToast(photoCaption.trim() ? "✓ Photo + caption saved" : "✓ Photo saved");
        const sr = await fetch(`${BACKEND_URL}/api/field/session/${session.session_id}`, fetchOpts());
        if (sr.ok) {
          const sd = await sr.json();
          setSession(sd.session);
        }
        cancelPhoto();
      } else {
        showToast(d.detail || "Photo upload failed", "error");
      }
    } catch {
      showToast("Photo upload failed", "error");
    }
    setPhotoUploading(false);
  };

  // ─── AI Plan Restructure ───
  const [restructuring, setRestructuring] = useState(false);
  const askAiRestructure = async () => {
    setRestructuring(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/plan-restructure`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ field_session_id: session.session_id, extra_context: endNotes || "" }),
      });
      const d = await r.json();
      if (r.ok) {
        const s = d.suggestion?.parsed || {};
        showToast(`✓ Suggestion ready: "${(s.summary || "saved").slice(0, 70)}…" — review on Projects tab.`);
      } else {
        showToast(d.detail || "Restructure failed", "error");
      }
    } catch {
      showToast("Restructure failed", "error");
    }
    setRestructuring(false);
  };

  // ─── End shift ───
  const endShift = async () => {
    if (!window.confirm("End this shift and generate the report?")) return;
    try {
      const r = await fetch(`${BACKEND_URL}/api/field/session/${session.session_id}/end`, {
        ...fetchOpts({ method: "POST" }),
        body: JSON.stringify({ notes: endNotes }),
      });
      const d = await r.json();
      if (r.ok) {
        setEndedResult(d);
        showToast("✓ Report generated");
      } else {
        showToast(d.detail || "Could not end shift", "error");
      }
    } catch {
      showToast("Could not end shift", "error");
    }
  };

  const downloadPdf = () => {
    if (!endedResult?.pdf_base64) return;
    const blob = new Blob(
      [Uint8Array.from(atob(endedResult.pdf_base64), c => c.charCodeAt(0))],
      { type: "application/pdf" }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `MF-Field-Report-${session.session_id.slice(-8)}.pdf`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  // ─── Render: crew gate ───
  if (isCrewLaunch && !crewAuthed) {
    return (
      <div style={crewGateStyle}>
        <div style={crewCardStyle}>
          <div style={{ textAlign: "center", marginBottom: 22 }}>
            <div style={{ fontSize: 42, marginBottom: 4 }}>🛠️</div>
            <h1 style={{ color: "#fff", fontSize: 22, margin: "0 0 4px", fontWeight: 800 }}>Moovin Forward</h1>
            <p style={{ color: "#5AC8D8", fontSize: 14, margin: 0, fontWeight: 700 }}>Field Mode · Crew Access</p>
          </div>
          <p style={{ color: "#ADBED1", fontSize: 14, marginBottom: 18, textAlign: "center" }}>
            Type your name to join this shift.
          </p>
          <input
            type="text"
            placeholder="Your name (required)"
            value={crewName}
            onChange={e => setCrewName(e.target.value)}
            data-testid="crew-name-input"
            style={{ ...mobileInput, marginBottom: 12 }}
          />
          <input
            type="tel"
            placeholder="Phone (optional)"
            value={crewPhone}
            onChange={e => setCrewPhone(e.target.value)}
            data-testid="crew-phone-input"
            style={{ ...mobileInput, marginBottom: 14 }}
          />
          {crewError && (
            <div data-testid="crew-error" style={{ padding: "10px 14px", background: "rgba(239,68,68,0.10)", border: "1px solid #ef4444", borderRadius: 8, color: "#fca5a5", fontSize: 13, marginBottom: 14 }}>
              {crewError}
            </div>
          )}
          <button
            onClick={handleCrewJoin}
            data-testid="crew-join-btn"
            style={primaryBtn}
          >
            Join Shift →
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: "#030B1A", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 16 }}>
        Loading Field Mode...
      </div>
    );
  }

  if (!session) {
    return (
      <div style={{ minHeight: "100vh", background: "#030B1A", padding: 24, color: "#fff" }}>
        <p>Could not start session.</p>
        <button onClick={() => navigate("/dashboard")} style={primaryBtn}>Back to Dashboard</button>
      </div>
    );
  }

  // ─── Shift ended view ───
  if (endedResult) {
    const total = (endedResult.session?.materials_tallied || []).reduce((s, m) => s + (m.subtotal || 0), 0);
    return (
      <div style={pageStyle}>
        <div style={{ maxWidth: 560, margin: "0 auto", padding: "24px 16px", textAlign: "center" }}>
          <div style={{ fontSize: 56, marginBottom: 10 }}>✅</div>
          <h1 style={{ color: "#fff", fontSize: 24, margin: "0 0 6px" }}>Shift Complete</h1>
          <p style={{ color: "#ADBED1", fontSize: 14, marginBottom: 24 }}>
            Report generated and emailed to Moovin Forward HQ.
          </p>
          <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(0,220,120,0.30)", borderRadius: 12, padding: "18px 20px", marginBottom: 20, textAlign: "left" }}>
            <Row label="Crew" value={endedResult.session?.crew_name} />
            <Row label="Room/Area" value={endedResult.session?.room_type} />
            <Row label="Materials" value={`${(endedResult.session?.materials_tallied || []).length} items · $${total.toFixed(2)}`} />
            <Row label="Photos" value={endedResult.session?.photos_count || 0} />
          </div>
          <button onClick={downloadPdf} data-testid="download-pdf-btn" style={{ ...primaryBtn, marginBottom: 10 }}>
            ⬇ Download PDF Report
          </button>
          {isAdminLaunch && (
            <button onClick={() => navigate("/dashboard")} style={ghostBtn}>← Back to Dashboard</button>
          )}
          {isCrewLaunch && (
            <p style={{ color: "#7E92A8", fontSize: 13, marginTop: 18 }}>
              You can close this tab. Thank you for your work today.
            </p>
          )}
        </div>
      </div>
    );
  }

  const specs = session?.specs_snapshot || {};
  const materials = session?.materials_tallied || [];
  const totalCost = materials.reduce((s, m) => s + (m.subtotal || 0), 0);

  return (
    <div style={pageStyle}>
      {isAdminLaunch && <PanicBackButton />}
      {toast && (
        <div data-testid="fm-toast" style={{ position: "fixed", top: 14, left: "50%", transform: "translateX(-50%)", zIndex: 1000, background: toast.kind === "error" ? "#7f1d1d" : "#064e3b", color: "#fff", padding: "10px 18px", borderRadius: 8, fontSize: 14, fontWeight: 700, border: `1px solid ${toast.kind === "error" ? "#ef4444" : "#10b981"}` }}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={{ position: "sticky", top: 0, background: "rgba(3,11,26,0.95)", backdropFilter: "blur(12px)", borderBottom: "1px solid rgba(255,255,255,0.08)", padding: "12px 16px", zIndex: 10 }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <DoctrineBanner compact />
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ color: "#5AC8D8", fontSize: 11, fontWeight: 800, letterSpacing: "0.10em", margin: "0 0 2px" }}>FIELD MODE · CAD/CAM</p>
              <h1 style={{ color: "#fff", fontSize: 16, fontWeight: 800, margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {project?.address || session?.project_address}
              </h1>
              <p style={{ color: "#ADBED1", fontSize: 12, margin: "2px 0 0" }}>
                👤 {session.crew_name} · {session.room_type}
              </p>
            </div>
            {/* Ship 3 · Offline Safe Mode pill */}
            {(!isOnline || queueSize > 0) && (
              <button
                type="button"
                data-testid="fm-offline-pill"
                onClick={() => { if (isOnline && queueSize > 0 && !draining) drain(); }}
                title={!isOnline ? "You're offline — writes are queued locally." : "Queued writes waiting to sync — tap to force-drain."}
                style={{
                  background: !isOnline ? "rgba(239,68,68,0.15)" : "rgba(245,158,11,0.18)",
                  border: `1px solid ${!isOnline ? "#ef4444" : "#F59E0B"}`,
                  color: !isOnline ? "#FCA5A5" : "#FCD34D",
                  padding: "6px 10px", borderRadius: 999,
                  fontSize: 11, fontWeight: 800, letterSpacing: "0.04em",
                  cursor: (isOnline && queueSize > 0 && !draining) ? "pointer" : "default",
                  whiteSpace: "nowrap",
                }}
              >
                {!isOnline
                  ? `📴 OFFLINE${queueSize > 0 ? ` · ${queueSize} queued` : ""}`
                  : draining
                    ? `🔄 Syncing ${queueSize}…`
                    : `⏳ ${queueSize} to sync — tap`}
              </button>
            )}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 720, margin: "0 auto", padding: "16px" }}>
        {/* Live totals */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 16 }}>
          <StatTile label="Tallied" value={materials.length} color="#00DC78" />
          <StatTile label="Cost" value={`$${totalCost.toFixed(0)}`} color="#5AC8D8" />
          <StatTile label="Photos" value={session.photos_count || 0} color="#A78BFA" />
        </div>

        {/* Big action grid */}
        {view === "dashboard" && (
          <>
          {/* PRIMARY — Ultra-Wide Scan (Hunter Mode). One button, no wizard. */}
          <button data-testid="action-hunter-scan"
                  onClick={() => setHunterOpen(true)}
                  style={{ width: "100%", padding: "22px 16px", marginBottom: 12,
                           background: "linear-gradient(135deg, #00FFFF 0%, #5AC8D8 100%)",
                           color: "#0b1220", border: "none", borderRadius: 12,
                           fontWeight: 900, fontSize: 17, letterSpacing: "0.05em",
                           cursor: "pointer",
                           boxShadow: "0 10px 26px rgba(0,255,255,0.35)",
                           display: "flex", flexDirection: "column",
                           alignItems: "center", gap: 4 }}>
            <div style={{ fontSize: 26 }}>📸</div>
            <div>ULTRA-WIDE SCAN</div>
            <div style={{ fontSize: 11, opacity: 0.75, fontWeight: 700, letterSpacing: "0.1em" }}>
              PHOTOS → DEFECT LEDGER → PACKET 1
            </div>
          </button>

          {/* SECONDARY — Existing field tools, compact so they don't compete with the scan */}
          <div data-testid="fm-dashboard" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 16 }}>
            <BigButton testId="action-specs" icon="📐" label="Pull Specs" color="#A78BFA" onClick={() => setView("specs")} />
            <BigButton testId="action-tally" icon="📋" label="Tally Materials" color="#00DC78" onClick={() => setView("tally")} />
            <BigButton testId="action-render" icon="🎨" label="Render Variation" color="#B8915A" onClick={() => setRenderOpen(true)} />
            <BigButton testId="action-end" icon="✅" label="End Shift" color="#F59E0B" onClick={() => setView("end")} />
          </div>

          {/* FWA Expo attendee feed — admin only */}
          {isAdminLaunch && (
            <div data-testid="fm-fwa-feed" style={{
              marginBottom: 16, padding: "12px 14px",
              background: "rgba(0,255,255,0.05)", border: "1px solid rgba(0,255,255,0.30)",
              borderRadius: 10,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div>
                  <div style={{ color: "#00FFFF", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.66rem", letterSpacing: "0.22em", fontWeight: 800 }}>
                    &gt; FWA_EXPO · RECENT SCANS
                  </div>
                  <div style={{ color: "#fff", fontSize: "0.9rem", fontWeight: 700, marginTop: 3 }}>
                    {attendees.length} attendee{attendees.length === 1 ? "" : "s"} captured
                  </div>
                </div>
                <button data-testid="fm-fwa-refresh" onClick={loadAttendees}
                        style={{ background: "transparent", color: "#00FFFF", border: "1px solid #00FFFF66",
                                 borderRadius: 4, padding: "0.35rem 0.7rem",
                                 fontFamily: "'JetBrains Mono', monospace", fontSize: "0.66rem",
                                 fontWeight: 700, letterSpacing: "0.1em", cursor: "pointer" }}>
                  ↻ REFRESH
                </button>
              </div>
              {attendeesLoading && attendees.length === 0 && (
                <div style={{ color: "#7E92A8", fontSize: "0.82rem", padding: "0.5rem 0" }}>Loading…</div>
              )}
              {!attendeesLoading && attendees.length === 0 && (
                <div data-testid="fm-fwa-empty" style={{ color: "#7E92A8", fontSize: "0.82rem", padding: "0.5rem 0", lineHeight: 1.5 }}>
                  No scans captured yet. Attendees will appear here as soon as they use the QR at your booth.
                </div>
              )}
              {attendees.slice(0, 8).map((a) => (
                <div key={a.attendee_id || a.captured_at} data-testid={`fm-fwa-row-${a.attendee_id}`}
                     style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start",
                              padding: "0.55rem 0", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ color: "#fff", fontSize: "0.88rem", fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {a.full_name || "—"}
                    </div>
                    <div style={{ color: "#7E92A8", fontSize: "0.72rem", marginTop: 2, lineHeight: 1.35 }}>
                      {a.affiliation_label || a.affiliation} · {a.company || "no company"}{a.job_title ? ` · ${a.job_title}` : ""}
                    </div>
                    {(a.email || a.phone) && (
                      <div style={{ color: "#5AC8D8", fontSize: "0.72rem", marginTop: 2 }}>
                        {a.email && <a href={`mailto:${a.email}`} style={{ color: "#5AC8D8", marginRight: 8 }}>{a.email}</a>}
                        {a.phone && <a href={`tel:${a.phone}`} style={{ color: "#5AC8D8" }}>{a.phone}</a>}
                      </div>
                    )}
                  </div>
                  <div style={{ color: "#00FFFF", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.62rem", letterSpacing: "0.1em", whiteSpace: "nowrap", marginLeft: 8 }}>
                    {a.captured_at ? new Date(a.captured_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
                  </div>
                </div>
              ))}
              {attendees.length > 8 && (
                <div style={{ color: "#7E92A8", fontSize: "0.72rem", marginTop: 6, textAlign: "center" }}>
                  + {attendees.length - 8} more
                </div>
              )}
            </div>
          )}
          </>
        )}

        {/* Specs view */}
        {view === "specs" && (
          <SectionCard title="📐 Project Specs" onBack={() => setView("dashboard")}>
            {/* Room Dimensions + Auto Material Calculator */}
            <div data-testid="room-calc-panel" style={{ padding: "14px", background: "rgba(0,220,120,0.06)", border: "1px solid rgba(0,220,120,0.30)", borderRadius: 10, marginBottom: 18 }}>
              <h3 style={{ color: "#00DC78", fontSize: 14, margin: "0 0 4px", fontWeight: 800 }}>
                📐 Room Calculator (CAM Auto-Tally)
              </h3>
              <p style={{ color: "#ADBED1", fontSize: 12, margin: "0 0 12px", lineHeight: 1.45 }}>
                Enter the room dimensions — system calculates paint, flooring, fixtures, hardware, caulk, drywall patches. One-tap adds everything to your shift tally.
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, marginBottom: 8 }}>
                <div>
                  <Label>Length (ft)</Label>
                  <input type="number" min="0" step="0.5" value={calcForm.length_ft} onChange={e => setCalcForm({ ...calcForm, length_ft: parseFloat(e.target.value) || 0 })} data-testid="calc-length" style={mobileInput} />
                </div>
                <div>
                  <Label>Width (ft)</Label>
                  <input type="number" min="0" step="0.5" value={calcForm.width_ft} onChange={e => setCalcForm({ ...calcForm, width_ft: parseFloat(e.target.value) || 0 })} data-testid="calc-width" style={mobileInput} />
                </div>
                <div>
                  <Label>Ceiling (ft)</Label>
                  <input type="number" min="7" step="0.5" value={calcForm.ceiling_ft} onChange={e => setCalcForm({ ...calcForm, ceiling_ft: parseFloat(e.target.value) || 9 })} data-testid="calc-ceiling" style={mobileInput} />
                </div>
              </div>
              <Label>Room Type</Label>
              <select value={calcForm.room_type} onChange={e => setCalcForm({ ...calcForm, room_type: e.target.value })} data-testid="calc-room-type" style={{ ...mobileInput, marginBottom: 10 }}>
                <option value="bedroom" style={{ background: "#030B1A" }}>Bedroom</option>
                <option value="living" style={{ background: "#030B1A" }}>Living Room</option>
                <option value="kitchen" style={{ background: "#030B1A" }}>Kitchen</option>
                <option value="bathroom" style={{ background: "#030B1A" }}>Bathroom</option>
                <option value="hallway" style={{ background: "#030B1A" }}>Hallway / Other</option>
              </select>
              <button onClick={runCalculator} disabled={calcLoading} data-testid="calc-run-btn" style={{ ...primaryBtn, opacity: calcLoading ? 0.6 : 1 }}>
                {calcLoading ? "Calculating..." : "🧮 Calculate Materials"}
              </button>

              {calcResult && (
                <div data-testid="calc-result" style={{ marginTop: 14, padding: "10px 12px", background: "rgba(0,0,0,0.20)", borderRadius: 8 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, flexWrap: "wrap", gap: 6 }}>
                    <div>
                      <p style={{ color: "#fff", fontSize: 13, fontWeight: 800, margin: 0 }}>
                        {calcResult.totals.floor_sqft} sqft floor · {calcResult.totals.paintable_wall_sqft} sqft walls
                      </p>
                      <p style={{ color: "#00DC78", fontSize: 18, fontWeight: 800, margin: "2px 0 0" }}>
                        Total: ${calcResult.totals.total_cost} · {calcResult.totals.item_count} items
                      </p>
                    </div>
                    <button onClick={addAllCalcItems} data-testid="calc-add-all-btn" style={{ background: "linear-gradient(135deg, #F59E0B 0%, #EF4444 100%)", color: "#fff", border: "none", padding: "8px 14px", borderRadius: 6, fontSize: 12, fontWeight: 800, cursor: "pointer", whiteSpace: "nowrap" }}>
                      ⚡ Add ALL to Tally
                    </button>
                  </div>
                  {/* Push to Pro Desk */}
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10, padding: "8px 10px", background: "rgba(243,99,2,0.06)", border: "1px dashed rgba(243,99,2,0.30)", borderRadius: 6 }}>
                    <p style={{ color: "#FCA5A5", fontSize: 10, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", margin: "0 0 4px", width: "100%" }}>📤 Push Cart to Pro Desk</p>
                    <button onClick={() => pushToProDesk("home_depot")} data-testid="push-hd-btn" style={{ flex: 1, background: "#F96302", color: "#fff", border: "none", borderRadius: 5, padding: "7px 8px", fontSize: 11, fontWeight: 800, cursor: "pointer", whiteSpace: "nowrap" }}>
                      🏠 Home Depot
                    </button>
                    <button onClick={() => pushToProDesk("lowes")} data-testid="push-lowes-btn" style={{ flex: 1, background: "#004990", color: "#fff", border: "none", borderRadius: 5, padding: "7px 8px", fontSize: 11, fontWeight: 800, cursor: "pointer", whiteSpace: "nowrap" }}>
                      🔵 Lowe&apos;s
                    </button>
                    <button onClick={() => pushToProDesk("wayfair")} data-testid="push-wayfair-btn" style={{ flex: 1, background: "#7B189F", color: "#fff", border: "none", borderRadius: 5, padding: "7px 8px", fontSize: 11, fontWeight: 800, cursor: "pointer", whiteSpace: "nowrap" }}>
                      💜 Wayfair
                    </button>
                  </div>
                  <div style={{ maxHeight: 320, overflowY: "auto" }}>
                    {calcResult.items.map((item, i) => (
                      <div key={i} data-testid={`calc-item-${i}`} style={{ padding: "8px 10px", marginBottom: 5, background: "rgba(255,255,255,0.03)", borderRadius: 6, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ color: "#fff", fontSize: 12, fontWeight: 700, margin: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{item.name}</p>
                          <p style={{ color: "#5AC8D8", fontSize: 11, margin: "1px 0 0" }}>
                            {item.quantity} {item.unit} × ${item.unit_cost} = <strong style={{ color: "#00DC78" }}>${item.subtotal}</strong>
                          </p>
                          <p style={{ color: "#7E92A8", fontSize: 10, margin: "1px 0 0", fontStyle: "italic" }}>{item.formula}</p>
                        </div>
                        <button onClick={() => quickAddCalc(item)} data-testid={`calc-add-${i}`} style={{ background: "rgba(0,220,120,0.15)", color: "#00DC78", border: "1px solid #00DC78", borderRadius: 5, padding: "4px 8px", fontSize: 10, fontWeight: 800, cursor: "pointer", whiteSpace: "nowrap" }}>
                          + Add
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {specs.palette_name && (
              <div style={{ marginBottom: 14 }}>
                <Label>Variation</Label>
                <p style={{ color: "#fff", fontSize: 14, fontWeight: 700, margin: "4px 0 8px" }}>{specs.palette_name}</p>
                {Array.isArray(specs.palette_swatches) && specs.palette_swatches.length > 0 && (
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {specs.palette_swatches.map((sw, i) => (
                      <div key={i} style={{ background: typeof sw === "string" ? sw : sw.hex || "#999", width: 44, height: 44, borderRadius: 6, border: "2px solid rgba(255,255,255,0.10)", display: "flex", alignItems: "flex-end", justifyContent: "center", color: "#fff", fontSize: 9, fontWeight: 800, paddingBottom: 2, textShadow: "0 0 4px rgba(0,0,0,0.8)" }}>
                        {typeof sw === "object" ? sw.label?.slice(0, 4) : ""}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {(specs.compliance_safe || specs.renter_friendly || specs.pre_1978 || specs.has_pool) && (
              <div style={{ marginBottom: 14 }}>
                <Label>Compliance Flags</Label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 4 }}>
                  {specs.compliance_safe && <FlagPill label="HUD HQS / NSPIRE Safe" color="#00DC78" />}
                  {specs.renter_friendly && <FlagPill label="Renter-Friendly" color="#5AC8D8" />}
                  {specs.pre_1978 && <FlagPill label="⚠ Pre-1978 (Lead Test Required)" color="#EF4444" />}
                  {specs.has_pool && <FlagPill label="⚠ Pool On Site" color="#0EA5E9" />}
                </div>
              </div>
            )}
            {Array.isArray(specs.sourced_products) && specs.sourced_products.length > 0 && (
              <div>
                <Label>Sourced Materials List ({specs.sourced_products.length})</Label>
                <div style={{ marginTop: 6, maxHeight: 280, overflowY: "auto" }}>
                  {specs.sourced_products.map((p, i) => (
                    <div key={i} style={{ padding: "10px 12px", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 6, marginBottom: 6, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ color: "#fff", fontSize: 13, fontWeight: 700, margin: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{p.name}</p>
                        {p.supplier && <p style={{ color: "#7E92A8", fontSize: 11, margin: "1px 0 0" }}>{p.supplier} {p.sku ? `· ${p.sku}` : ""}</p>}
                      </div>
                      <button onClick={() => quickAddSpec(p)} data-testid={`spec-quickadd-${i}`} style={{ background: "rgba(0,220,120,0.15)", color: "#00DC78", border: "1px solid #00DC78", borderRadius: 6, padding: "5px 10px", fontSize: 11, fontWeight: 800, cursor: "pointer", whiteSpace: "nowrap" }}>
                        + Tally
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {!specs.palette_name && (!specs.sourced_products || specs.sourced_products.length === 0) && (
              <p style={{ color: "#ADBED1", fontSize: 13 }}>No specs found on this project yet. Run the Design Studio + add items to My Products first.</p>
            )}
          </SectionCard>
        )}

        {/* Tally view */}
        {view === "tally" && (
          <SectionCard title="📋 Tally Material Used" onBack={() => setView("dashboard")}>
            <Label>Item Name *</Label>
            <input value={tallyForm.name} onChange={e => setTallyForm({ ...tallyForm, name: e.target.value })} data-testid="tally-name" placeholder="Sherwin-Williams Eider White" style={mobileInput} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 8 }}>
              <div>
                <Label>Quantity</Label>
                <input type="number" min="0" step="0.5" value={tallyForm.quantity} onChange={e => setTallyForm({ ...tallyForm, quantity: parseFloat(e.target.value) || 0 })} data-testid="tally-qty" style={mobileInput} />
              </div>
              <div>
                <Label>Unit Cost ($)</Label>
                <input type="number" min="0" step="0.01" value={tallyForm.unit_cost} onChange={e => setTallyForm({ ...tallyForm, unit_cost: parseFloat(e.target.value) || 0 })} data-testid="tally-cost" style={mobileInput} />
              </div>
            </div>
            <button onClick={addTally} data-testid="tally-add-btn" style={{ ...primaryBtn, marginTop: 12 }}>
              + Add to Tally
            </button>

            {materials.length > 0 && (
              <div style={{ marginTop: 18 }}>
                <Label>Running List ({materials.length})</Label>
                <div style={{ marginTop: 6, maxHeight: 300, overflowY: "auto" }}>
                  {materials.slice().reverse().map((m) => (
                    <div key={m.entry_id} style={{ padding: "8px 10px", background: "rgba(255,255,255,0.02)", borderRadius: 6, marginBottom: 4, fontSize: 12, color: "#ADBED1" }}>
                      <strong style={{ color: "#fff" }}>{m.name}</strong> · {m.quantity} × ${m.unit_cost.toFixed(2)} = <span style={{ color: "#00DC78" }}>${m.subtotal.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                <p style={{ color: "#fff", fontSize: 14, fontWeight: 800, marginTop: 10, textAlign: "right" }}>
                  Total: <span style={{ color: "#00DC78" }}>${totalCost.toFixed(2)}</span>
                </p>
              </div>
            )}
          </SectionCard>
        )}

        {/* End shift view */}
        {view === "end" && (
          <SectionCard title="✅ End Shift & Generate Report" onBack={() => setView("dashboard")}>
            <Label>Crew Notes (optional)</Label>
            <textarea value={endNotes} onChange={e => setEndNotes(e.target.value)} rows={4} data-testid="end-notes" placeholder="Anything Shannon needs to know — issues found, follow-ups needed..." style={{ ...mobileInput, fontFamily: "inherit" }} />
            <button onClick={endShift} data-testid="end-shift-btn" style={{ ...primaryBtn, marginTop: 12, background: "linear-gradient(135deg, #F59E0B 0%, #EF4444 100%)" }}>
              Generate PDF Report → Email Shannon
            </button>
          </SectionCard>
        )}

        <input ref={fileInputRef} type="file" accept="image/*" capture="environment" onChange={onPhotoSelected} style={{ display: "none" }} data-testid="photo-input" />

        {/* Voice-caption photo modal — crew speaks the issue into the photo */}
        {photoPending && (
          <div data-testid="photo-caption-modal"
               onClick={(e) => { if (e.target === e.currentTarget) cancelPhoto(); }}
               style={{ position: "fixed", inset: 0, background: "rgba(8,13,28,0.85)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        zIndex: 9500, padding: 16 }}>
            <div style={{ background: "#0f172a", border: "1px solid rgba(184,145,90,0.45)",
                          borderLeft: "4px solid #B8915A", borderRadius: 10,
                          padding: "1.2rem 1.3rem", width: "100%", maxWidth: 480,
                          maxHeight: "92vh", overflowY: "auto",
                          boxShadow: "0 24px 60px rgba(0,0,0,0.55)" }}>
              <div style={{ color: "#B8915A", fontSize: 11, letterSpacing: "0.18em",
                            textTransform: "uppercase", fontWeight: 700, marginBottom: 6 }}>
                📸 Caption This Photo
              </div>
              <div style={{ color: "#F3F4F6", fontWeight: 700, fontSize: 16, marginBottom: 10 }}>
                {typeOnlyMode ? "Type the issue." : "Speak the issue. Or type it. Or both."}
              </div>
              <img src={photoPending.dataUrl} alt="preview"
                   data-testid="photo-preview"
                   style={{ width: "100%", maxHeight: 240, objectFit: "cover", borderRadius: 8,
                            border: "1px solid rgba(255,255,255,0.08)", marginBottom: 10 }} />

              {!typeOnlyMode && (
                <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                  <button type="button" onClick={togglePhotoListening}
                          data-testid="photo-voice-toggle"
                          style={{ flex: 1, padding: "12px",
                                   background: photoListening ? "#FCA5A5" : "#86EFAC",
                                   color: "#0F172A", border: "none", borderRadius: 8,
                                   fontWeight: 800, fontSize: 15, cursor: "pointer" }}>
                    {photoListening ? "⏹ Stop Listening" : "🎙️ Speak the Issue"}
                  </button>
                  <button type="button" onClick={switchToTypeOnly}
                          data-testid="photo-type-instead"
                          style={{ flex: "0 0 auto", padding: "12px 14px",
                                   background: "transparent",
                                   color: "#5AC8D8",
                                   border: "1px solid rgba(90,200,216,0.55)",
                                   borderRadius: 8,
                                   fontWeight: 800, fontSize: 14, cursor: "pointer",
                                   whiteSpace: "nowrap" }}>
                    ⌨️ Type Instead
                  </button>
                </div>
              )}

              {typeOnlyMode && (
                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 6 }}>
                  <button type="button"
                          data-testid="photo-voice-reenable"
                          onClick={() => setTypeOnlyMode(false)}
                          style={{ background: "transparent", color: "#86EFAC",
                                   border: "1px solid rgba(134,239,172,0.5)",
                                   padding: "4px 10px", borderRadius: 999,
                                   fontSize: 11, fontWeight: 700, cursor: "pointer",
                                   letterSpacing: "0.06em" }}>
                    🎙 Turn voice back on
                  </button>
                </div>
              )}

              <textarea
                ref={photoCaptionRef}
                value={photoCaption}
                onChange={(e) => setPhotoCaption(e.target.value)}
                placeholder="e.g., 'Kitchen ceiling has water staining around the recessed light, leak appears recent, drywall is soft.'"
                rows={typeOnlyMode ? 5 : 3}
                data-testid="photo-caption-input"
                style={{ width: "100%", background: "#1f2937",
                         border: `1px solid ${typeOnlyMode ? "#5AC8D8" : "#374151"}`, color: "#F3F4F6",
                         padding: "10px 12px", borderRadius: 6, fontSize: 14,
                         resize: "vertical", fontFamily: "inherit", marginBottom: 10 }} />

              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button type="button" onClick={cancelPhoto}
                        data-testid="photo-cancel"
                        style={{ background: "transparent", color: "#9CA3AF",
                                 border: "1px solid #4B5563", padding: "10px 18px",
                                 borderRadius: 6, fontWeight: 700, fontSize: 14, cursor: "pointer" }}>
                  Cancel
                </button>
                <button type="button" onClick={savePhotoWithCaption}
                        disabled={photoUploading}
                        data-testid="photo-save"
                        style={{ background: photoUploading ? "#4B5563" : "#B8915A",
                                 color: "#1b2a4e", border: "1px solid #8a6c3e",
                                 padding: "10px 22px", borderRadius: 6,
                                 fontWeight: 800, fontSize: 14,
                                 cursor: photoUploading ? "not-allowed" : "pointer" }}>
                  {photoUploading ? "Uploading…" : "✓ Save Photo"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Photo → Variation renderer modal (field-side) */}
        <PropertyPhotoRender open={renderOpen} onClose={() => setRenderOpen(false)} />
        <HunterScanModal open={hunterOpen} onClose={() => setHunterOpen(false)}
                         defaultAddress={project?.property_address || ""}
                         defaultUnit={project?.unit || ""} />
      </div>
    </div>
  );
};

// =============================================================================
// Small subcomponents
// =============================================================================
const BigButton = ({ icon, label, color, onClick, testId }) => (
  <button onClick={onClick} data-testid={testId} style={{
    padding: "22px 14px", background: "rgba(255,255,255,0.03)",
    border: `2px solid ${color}`, borderRadius: 12, color: "#fff",
    fontSize: 14, fontWeight: 800, cursor: "pointer", textAlign: "center",
    transition: "transform 0.1s",
  }}>
    <div style={{ fontSize: 32, marginBottom: 6 }}>{icon}</div>
    {label}
  </button>
);

const StatTile = ({ label, value, color }) => (
  <div style={{ padding: "10px 12px", background: "rgba(255,255,255,0.03)", border: `1px solid ${color}40`, borderRadius: 8, textAlign: "center" }}>
    <p style={{ color, fontSize: 18, fontWeight: 800, margin: 0 }}>{value}</p>
    <p style={{ color: "#7E92A8", fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", margin: "2px 0 0" }}>{label}</p>
  </div>
);

const SectionCard = ({ title, onBack, children }) => (
  <div style={{ background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: "16px" }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
      <h2 style={{ color: "#fff", fontSize: 16, fontWeight: 800, margin: 0 }}>{title}</h2>
      <button onClick={onBack} data-testid="back-btn" style={{ background: "transparent", color: "#5AC8D8", border: "1px solid rgba(0,255,255,0.30)", borderRadius: 6, padding: "5px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>← Back</button>
    </div>
    {children}
  </div>
);

const Label = ({ children }) => (
  <label style={{ display: "block", color: "#ADBED1", fontSize: 11, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 3 }}>
    {children}
  </label>
);

const Row = ({ label, value }) => (
  <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid rgba(255,255,255,0.05)", fontSize: 14 }}>
    <span style={{ color: "#7E92A8", fontWeight: 700 }}>{label}</span>
    <span style={{ color: "#fff", fontWeight: 700 }}>{value}</span>
  </div>
);

const FlagPill = ({ label, color }) => (
  <span style={{ padding: "5px 10px", background: `${color}20`, border: `1px solid ${color}80`, color, borderRadius: 6, fontSize: 11, fontWeight: 800 }}>
    {label}
  </span>
);

// Styles
const pageStyle = { minHeight: "100vh", background: "#030B1A", color: "#fff", paddingBottom: 40 };
const crewGateStyle = { minHeight: "100vh", background: "linear-gradient(135deg, #030B1A 0%, #0a1929 100%)", display: "flex", alignItems: "center", justifyContent: "center", padding: 18 };
const crewCardStyle = { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(0,255,255,0.20)", borderRadius: 16, padding: "32px 26px", maxWidth: 380, width: "100%" };
const mobileInput = { width: "100%", padding: "13px 14px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.04)", color: "#fff", fontSize: 15, boxSizing: "border-box", outline: "none" };
const primaryBtn = { width: "100%", padding: "14px 18px", borderRadius: 8, border: "none", background: "linear-gradient(135deg, #00DC78 0%, #00FFFF 100%)", color: "#030B1A", fontSize: 15, fontWeight: 800, cursor: "pointer" };
const ghostBtn = { background: "transparent", color: "#fff", border: "1px solid rgba(255,255,255,0.20)", padding: "12px 16px", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer" };

export default FieldMode;
